import React, { useMemo } from 'react'

import { Button, Grid, Typography } from '@barracuda-internal/bds-core'
import { EmailSearch } from '@barracuda-internal/bds-core/dist/Icons/Email'

import LinearProgress from 'global/components/lib/linearProgress/LinearProgress'
import Tabs from 'global/components/lib/Tabs/Tabs'
import { useFormatMessage } from 'global/lib/localization'
import { config } from 'global/lib/config'

import DashboardAttackingDomains from 'ets/components/pages/dashboard/attackingDomains/DashboardAttackingDomains'
import DashboardDomains from 'ets/components/pages/dashboard/domains/DashboardDomains'
import DashboardEmployees from 'ets/components/pages/dashboard/employees/DashboardEmployees'
import DashboardHeader from 'ets/components/pages/dashboard/header/DashboardHeader'
import DashboardMenu from 'ets/components/pages/dashboard/menu/DashboardMenu'
import DashboardOverview from 'ets/components/pages/dashboard/overview/DashboardOverview'
import DashboardThreats from 'ets/components/pages/dashboard/threats/DashboardThreats'
import useDashboardLogic, { TABS, APP_HEADER_HEIGHT } from 'ets/components/pages/dashboard/useDashboardLogic'
import styles from 'ets/components/pages/dashboard/dashboardStyles'
import Layout from 'ets/components/lib/layout/Layout'
import { ConvertToFullProductDialog } from 'ets/components/lib/dialogs/convertToFullProductDialog/ConvertToFullProductDialog'

const BASE_I18N_KEY = 'ets.app.dashboard'

export const Dashboard: React.FC = () => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const classes = styles()
  const [dashboardLogic] = useDashboardLogic()

  return useMemo(() => {
    const {
      getSectionElement,
      hasSenLicense,
      isFreeTrialButtonHidden,
      inProgress,
      isNewScan,
      isRerunScanInProgress,
      isFromSharedLink,
      isUserInputDisabledForTable,
      fixTableHeight,
      onNewScan,
      scanCount,
      scrollToSection,
      tabsConfig
    } = dashboardLogic

    return (
      <Layout
        isFromSharedLink={isFromSharedLink}
        pageTitle={isNewScan ? formatMessage('page_title.new_scan') : formatMessage('page_title.scan_summary')}
        rightContent={
          hasSenLicense ? (
            <ConvertToFullProductDialog />
          ) : (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={`${classes.freeTrialButton} ${isFreeTrialButtonHidden && classes.hideFreeTrialButton}`}
              onClick={() => {
                window.open(config.emailProtectionStartTrialUrl, '_blank')
              }}
              data-action="free-trial"
            >
              {formatMessage('start_trial')}
            </Button>
          )
        }
      >
        {!isFromSharedLink && (
          <div className={classes.scanTabs}>
            <Tabs {...tabsConfig} />
          </div>
        )}
        <div style={{ marginBottom: '32px' }}>{inProgress && <LinearProgress />}</div>
        <Grid container alignItems="center" justifyContent="center" data-field="grid-root">
          {isNewScan ? (
            <Grid container direction="column" alignItems="center" spacing={2} justifyContent="center">
              <Grid item>
                <EmailSearch style={{ width: '58px', height: '58px', fill: '#7D7D7D' }} />
              </Grid>
              <Grid item>
                <Typography variant="body1" align="center">
                  <strong>{formatMessage('new_scan.new_scan_title', { count: scanCount })}</strong>
                </Typography>
                <Typography variant="body1" align="center">
                  {formatMessage('new_scan.new_scan_subtitle')}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  className={classes.startScanButton}
                  variant="contained"
                  color="primary"
                  onClick={onNewScan}
                  isLoading={isRerunScanInProgress}
                >
                  {formatMessage('new_scan.start_scan_button')}
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Grid container alignItems="center" justifyContent="center" direction="column" data-field="grid-inner-root">
              <div className={classes.innerRoot}>
                <DashboardHeader />
              </div>

              <DashboardMenu
                TABS={TABS}
                scrollToSection={scrollToSection}
                getSectionElement={getSectionElement}
                APP_HEADER_HEIGHT={APP_HEADER_HEIGHT}
              />
              <div className={`${classes.innerRoot} overview-section`}>
                <DashboardOverview scrollToSection={scrollToSection} />
              </div>
              <Grid container alignItems="center" justifyContent="center">
                <div className={classes.separator} />
              </Grid>
              <Grid className="employees-section" container justifyContent="center">
                <Typography className={classes.title}>{formatMessage('employees.title')}</Typography>
              </Grid>
              <div className={classes.innerRoot}>
                <DashboardEmployees
                  isUserInputDisabledForTable={isUserInputDisabledForTable}
                  fixTableHeight={fixTableHeight}
                />
              </div>
              <Grid container alignItems="center" justifyContent="center">
                <div className={classes.separator} />
              </Grid>
              <Grid className="threats-section" container justifyContent="center">
                <Typography className={classes.title}>{formatMessage('threats.title')}</Typography>
              </Grid>
              <div className={classes.innerRoot}>
                <DashboardThreats
                  isUserInputDisabledForTable={isUserInputDisabledForTable}
                  fixTableHeight={fixTableHeight}
                />
              </div>
              <Grid container alignItems="center" justifyContent="center">
                <div className={classes.separator} />
              </Grid>
              <Grid className="domains-section" container justifyContent="center">
                <Typography className={classes.title}>{formatMessage('domains.title')}</Typography>
              </Grid>
              <div className={classes.innerRoot}>
                <DashboardDomains
                  isUserInputDisabledForTable={isUserInputDisabledForTable}
                  fixTableHeight={fixTableHeight}
                />
              </div>
              <Grid container alignItems="center" justifyContent="center">
                <div className={classes.separator} />
              </Grid>
              <Grid className="attacks-section" container justifyContent="center">
                <Typography className={classes.title}>{formatMessage('attacks.title')}</Typography>
              </Grid>
              <div className={classes.innerRoot}>
                <DashboardAttackingDomains
                  isUserInputDisabledForTable={isUserInputDisabledForTable}
                  fixTableHeight={fixTableHeight}
                />
              </div>
              <Grid container alignItems="center" justifyContent="center">
                <div className={classes.separator} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Layout>
    )
  }, [dashboardLogic, classes, formatMessage])
}

export default Dashboard
